import { useState, useEffect } from 'react';
import useForm from '@usas/ui-web/utils/UseForm';
import validate from "./TimesDetailKendoReportValidate";
import useReportPeriodFlatData from '@usas/ui-web/state/reportPeriodFlat/UseReportPeriodFlatData';
import useTimeStandardTypeData from '@usas/ui-web/state/timeStandardType/UseTimeStandardTypeData';
import Constants from '@usas/ui-web/utils/Constants';
import ToIntIfInt from '@usas/ui-web/utils/ToIntIfInt';

import useOrgUnitData from '@usas/ui-web/state/orgUnitForPromise/UseOrgUnitData';

// Define the columns configuration for this report
const columns = [
  { width: "90px", id: "Rank", field: "rank", title: "Rank" },
  { width: "100px", id: "EventGender", field: "eventgender", title: "Competition Category" },
  { width: "100px", id: "EventCode", field: "eventcode", title: "Event" },
  { width: "110px", id: "SwimTime", field: "swimtime", title: "Swim Time" },
  { width: "135px", id: "MemberId", field: "memberid", title: "Member ID" },
  { width: "100px", id: "MiddleName", field: "middlename", title: "Middle Name" },
  { width: "100px", id: "FirstName", field: "firstname", title: "First Name" },
  { width: "100px", id: "LastName", field: "lastname", title: "Last Name" },
  { width: "90px", id: "LscCode", field: "lsccode", title: "LSC" },
  { width: "90px", id: "ClubCode", field: "clubcode", title: "Club Code" },
  { width: "150px", id: "ClubName", field: "clubname", title: "Club Name" },
  { width: "90px", id: "SwimmerAge", field: "swimmerage", title: "Age" },
  { width: "420px", id: "MeetName", field: "meetname", title: "Meet" },
  { width: "110px", id: "SwimDate", field: "swimdate", title: "Date", format: "{0:MM/dd/yyyy}" },
  { width: "90px", id: "TimeStandardShortType", field: "timestandardshorttype", title: "Standard" },
  { width: "100px", id: "PowerPoints", field: "powerpoints", title: "Power Points" }
];

const excelColumns = [
  { id: "Rank", field: "rank", title: "Rank" },
  { id: "EventGender", field: "eventgender", title: "Competition Category" },
  { id: "EventCode", field: "eventcode", title: "Event" },
  { id: "SwimTime", field: "swimtime", title: "Swim Time" },
  { id: "MemberId", field: "memberid", title: "Member ID" },
  { id: "FirstName", field: "firstname", title: "First Name" },
  { id: "LastName", field: "lastname", title: "Last Name" },
  { id: "LscCode", field: "lsccode", title: "LSC" },
  { id: "ClubCode", field: "clubcode", title: "Club Code" },
  { id: "ClubName", field: "clubname", title: "Club Name" },
  { id: "SwimmerAge", field: "swimmerage", title: "Age" },
  { id: "MeetName", field: "meetname", title: "Meet" },
  { id: "SwimDate", field: "swimdate", title: "Date", format: "{0:MM/dd/yyyy}" },
  { id: "TimeStandardShortType", field: "timestandardshorttype", title: "Standard" },
  { id: "PowerPoints", field: "powerpoints", title: "PowerPoints" },
  { id: "Splits_CumulativeDistance", field: "splits_cumulativeDistance", title: "Cumulative Distance" },
  { id: "Splits_CumulativeSplitTime", field: "splits_cumulativeSplitTime", title: "Cumulative Split Time" },
  { id: "Splits_SplitTime", field: "splits_splitTime", title: "Split Time" }
];

const useTimesDetailKendoReport = () => {
  const { reportPeriodFlatState, getReportPeriodsFlatByOrganizationId } = useReportPeriodFlatData();
  const [seasonYearState, setSeasonYearState] = useState({ isLoading: true, isLoaded: false, options: [] });
  const { timeStandardTypeState, getTimeStandardTypesByOrgUnitId } = useTimeStandardTypeData();
  const [filteredTimeStandardTypeState, setFilteredTimeStandardTypeState] = useState({ isLoading: true, isLoaded: false, options: [] });
  const [modalState, setModalState] = useState(getInitialModalState());

  const { orgUnitState, getOrgUnitByOrgUnitId } = useOrgUnitData();

  const {
    formState,
    errorState,
    handleSubmit,
    updateFormState,
    onFormValueChanged,
    onValueTextPairChanged,
    setFormState,
    setIsDirty
  } = useForm(getInitialFormState(), submitFormCallback, validate);

  function submitFormCallback() {
    setModalState({ ...modalState, displayPopUp: true });
  }

  const onRunReportClicked = (e) => {
    handleSubmit(e);
  }

  const onClearForm = () => {
    setFormState(getInitialFormState());
    setIsDirty(true);
  }

  const onModalCancelClicked = (e) => {
    if (e && e.preventDefault) {
      e.preventDefault();
    }
    setIsDirty(true);
    setModalState({ ...modalState, displayPopUp: false });
  };

  function getInitialModalState() {
    return {
      displayPopUp: false,
      currentPrimaryInfo: '',
      modalTitle: 'Report Viewer'
    };
  }

  function getInitialFormState() {
    return {
      orgUnitId: 3,
      courses: [],
      events: [],
      startDate: Constants.BLANK_DATE_STRING,
      endDate: Constants.BLANK_DATE_STRING,
      seasonId: -1,
      seasonYear: -1,
      minAge: 1,
      maxAge: 99,
      minTimeStandard: 12,
      maxTimeStandard: 1,
      minPowerPoints: 1,
      genders: [{ id: 1, name: 'Female' }, { id: 2, name: 'Male' }],
      repTeamUsa: false,
      excludeNcaa: false,
      excludeLeadoff: false,
      showOlyEvent: false,
      clubs: [],
      lscs: [],
      zones: [],
      topTimes: 10,
      displayOptions: true,
      nationalTeamGroupId: -1,
      nationalTeamGroupName: '',
      exportDetailData: false,
      memberId: '',
      personId: Constants.DEFAULT_ID
    };
  }

  useEffect(() => {
    if (reportPeriodFlatState.isLoading === false && reportPeriodFlatState.isLoaded === false) {
      getReportPeriodsFlatByOrganizationId(1);
    }

    if (timeStandardTypeState.isArrayLoading === false && timeStandardTypeState.isArrayLoaded === false) {
      getTimeStandardTypesByOrgUnitId(3);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (reportPeriodFlatState.isLoaded === true) {
      const years = [];

      reportPeriodFlatState.dataAsOptionsWithNames.map(rp => {
        if (rp.name.substring(5, 6) === '(') {
          years.unshift({ id: rp.id, name: rp.name.substring(0, 4) });
        }
      });
      years.unshift({ id: -1, name: '--' });
      setSeasonYearState({ isLoading: false, isLoaded: true, options: years });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reportPeriodFlatState]);

  useEffect(() => {
    if (timeStandardTypeState.isArrayLoaded === true) {
      const standardTypes = [];
      timeStandardTypeState.arrayData.map(st => {
        if (st.assignToTimes === true && st.availableToHq === true) {
          standardTypes.push({ id: st.sortOrder, name: st.typeName });
        }
      });
      setFilteredTimeStandardTypeState({ isLoading: false, isLoaded: true, options: standardTypes });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timeStandardTypeState]);

  useEffect(() => {
    if (filteredTimeStandardTypeState.isLoading === false && filteredTimeStandardTypeState.isLoaded === true) {
      setFormState({ ...formState, minTimeStandard: 12 });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filteredTimeStandardTypeState]);

  return {
    formState,
    errorState,
    setFormState,
    reportParameters: {
      Courses: formState.courses.map(course => course.id).join(',') || null, 
      Events: formState.events.map(event => event.id).join(',') || null, 
      StartDate: formState.startDate === Constants.BLANK_DATE_STRING ? null : formState.startDate, 
      EndDate: formState.endDate === Constants.BLANK_DATE_STRING ? null : formState.endDate, 
      SeasonYear_Int: formState.seasonYear === Constants.DEFAULT_ID ? null : parseInt(formState.seasonYear, 10), 
      MinAge_Int: ToIntIfInt(formState.minAge), 
      MaxAge_Int: ToIntIfInt(formState.maxAge), 
      MinTimeStandard_Int: formState.minTimeStandard, 
      MaxTimeStandard_Int: formState.maxTimeStandard, 
      MinPowerPoints_Int: ToIntIfInt(formState.minPowerPoints), 
      Genders: formState.genders.map(gender => gender.id).join(','), 
      RepTeamUsa_Int: formState.repTeamUsa === true ? 1 : 0, 
      ExcludeNcaa_Int: formState.excludeNcaa === true ? 1 : 0, 
      ExcludeLeadoff_Int: formState.excludeLeadoff === true ? 1 : 0, 
      Clubs: formState.clubs.map(club => club.id).join(',') || null, 
      LSCs: formState.lscs.map(lsc => lsc.id).join(',') || null, 
      Zones: formState.zones.map(zone => zone.id).join(',').length === 0 ? null : formState.zones.map(zone => zone.id).join(','), 
      TopTime_Int: ToIntIfInt(formState.topTimes), 
      DisplayOptions_Int: formState.displayOptions === true ? 1 : 0,
      MemberIdParam: formState.memberId || ''
    },
    parameters: formState,
    columns: columns,
    modalState,
    orgUnitState,
    onModalCancelClicked,
    handleSubmit,
    onFormValueChanged,
    onValueTextPairChanged,
    updateFormState,
    onRunReportClicked,
    onClearForm,
    seasonYearState,
    filteredTimeStandardTypeState,
    excelColumns: excelColumns
  };
};

export default useTimesDetailKendoReport;